import React from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import { FileExtensionResult } from 'stores/WorkSpaceStoreAdapter';

import ExtensionMenu from './ExtensionMenu';

export type ContextMenuState = {
  mouseX: number;
  mouseY: number;
  nodeId: string;
};

type Props = {
  state: ContextMenuState | null;
  /** 作成可能な拡張子一覧（「ファイルを作成」サブメニュー用） */
  extensions: FileExtensionResult[];
  onClose: () => void;
  onCreateFolder: () => void;
  /** 拡張子を選んでファイル作成（extension は 'java' などの小文字） */
  onCreateFile: (extension: string) => void;
  onRename: () => void;
  onDelete: () => void;
  /** 削除を非活性にするか（配下に子ノードがあるフォルダなど） */
  disableDelete?: boolean;
  /** 削除非活性時のツールチップ理由 */
  disableDeleteReason?: string;
};

/**
 * ツリーノードの右クリックコンテキストメニュー。
 * フォルダを作成 / ファイルを作成（拡張子サブメニュー）/ 名前の変更 / 削除。
 */
const NodeContextMenu: React.FC<Props> = ({
  state,
  extensions,
  onClose,
  onCreateFolder,
  onCreateFile,
  onRename,
  onDelete,
  disableDelete,
  disableDeleteReason,
}) => {
  // 「ファイルを作成」サブメニューのアンカー
  const [fileSubAnchor, setFileSubAnchor] = React.useState<HTMLElement | null>(null);

  const handleClose = () => {
    setFileSubAnchor(null);
    onClose();
  };

  return (
    <Menu
      open={state !== null}
      onClose={handleClose}
      anchorReference="anchorPosition"
      anchorPosition={state ? { top: state.mouseY, left: state.mouseX } : undefined}
      slotProps={{
        paper: {
          elevation: 0,
          sx: { border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 180 },
        },
      }}
    >
      <MenuItem
        onMouseEnter={() => setFileSubAnchor(null)}
        onClick={() => {
          onCreateFolder();
          handleClose();
        }}
      >
        <ListItemIcon>
          <CreateNewFolderOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>フォルダを作成</ListItemText>
      </MenuItem>

      {/* ファイルを作成（ホバーで拡張子サブメニューを表示） */}
      <MenuItem
        onMouseEnter={(e) => setFileSubAnchor(e.currentTarget)}
        selected={Boolean(fileSubAnchor)}
      >
        <ListItemIcon>
          <NoteAddOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>ファイルを作成</ListItemText>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Box>
      </MenuItem>

      <Divider />

      <MenuItem
        onMouseEnter={() => setFileSubAnchor(null)}
        onClick={() => {
          onRename();
          handleClose();
        }}
      >
        <ListItemIcon>
          <DriveFileRenameOutlineIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>名前の変更</ListItemText>
      </MenuItem>
      <Tooltip
        title={disableDelete ? (disableDeleteReason ?? '') : ''}
        placement="right"
        disableHoverListener={!disableDelete}
      >
        <span onMouseEnter={() => setFileSubAnchor(null)}>
          <MenuItem
            disabled={disableDelete}
            onClick={() => {
              onDelete();
              handleClose();
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}>
              削除
            </ListItemText>
          </MenuItem>
        </span>
      </Tooltip>

      {/* 拡張子選択サブメニュー（ホバーで横に展開） */}
      <ExtensionMenu
        submenu
        anchorEl={fileSubAnchor}
        extensions={extensions}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setFileSubAnchor(null)}
        onMenuLeave={() => setFileSubAnchor(null)}
        onSelect={(extension) => {
          onCreateFile(extension);
          handleClose();
        }}
      />
    </Menu>
  );
};

export default NodeContextMenu;
